/* components/patterns/EditMemberModal.jsx — изменение участника (T3, полиш T5).
   Статус (Участник/Администратор, Segmented) + Select «Роль» (роли ws пропсом сверху,
   пункт «Без роли (полный доступ)») + разворачиваемый блок «Индивидуальные исключения»:
   тристейт Наследовать/Разрешить/Запретить на каждый ключ каталога analyticsAccess
   (дефолт — Наследовать). Для администратора роль и матрица скрыты («Администратор видит всё»);
   при ПУСТОМ списке ролей ws Select и блок исключений тоже скрыты (как в InviteMemberModal) —
   остаётся только статус. onSave(member, patch) шлёт ТОЛЬКО изменённые поля (сборка —
   buildMemberPatch из adapters/settings.js, там же тесты D11): role — при явной смене статуса;
   role_id / permission_overrides (ПОЛНАЯ замена, {} очищает) — при изменении.
   Ошибка onSave НЕ закрывает модалку (набранная матрица сохраняется; тост кажет TeamTab).
   Полноэкранная на xs (Modal sheet). // заменяет: frontend/src/pages/settings/EditMemberModal.jsx. */

import React from 'react';
import { Button } from '../ui/Button.jsx';
import { Field, Labeled, Segmented } from '../ui/Field.jsx';
import { Modal } from '../ui/Modal.jsx';
import { Select } from '../ui/Select.jsx';
import { ACCESS_SECTIONS, ACCESS_TABS, accessKey } from '../../lib/analyticsAccess.js';
import { buildMemberPatch, overridesFromTriState, triStateFromOverrides } from '../../lib/adapters/settings.js';

  const { useState, useEffect } = React;
  // UI-enum статуса (адаптер teamReal конвертит role↔is_admin для бэка). Локально — без зависимости от mock/.
  const ROLE_OPTIONS = [
    { value: 'member', label: 'Участник' },
    { value: 'admin', label: 'Администратор' },
  ];
  const TRI_OPTIONS = [
    { value: 'inherit', label: 'Наследовать' },
    { value: 'allow', label: 'Разрешить' },
    { value: 'deny', label: 'Запретить' },
  ];
  const NO_ROLE = ''; // значение Select «Без роли (полный доступ)»

  const tabBoxStyle = {
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-10) var(--space-12)',
  };

  // Строка тристейта по одному ключу каталога: подпись сверху, компактный segmented (sm) снизу —
  // юзабельно на 380px (segmented не шире контейнера, лишнее уходит в горизонтальный скролл).
  // НЕ `compact`: этот модификатор прячет текстовые лейблы (icon-only), а тут иконок нет.
  function TriRow({ label, value, onChange, strong }) {
    return (
      <div className="stack stack-4">
        <span className={strong ? 't-label' : 't-caption'}>{label}</span>
        <Segmented size="sm" value={value || 'inherit'} onChange={onChange}
          options={TRI_OPTIONS} ariaLabel={'Доступ: ' + label} />
      </div>
    );
  }

  function EditMemberModal({ open, member, roles = [], onClose, onSave }) {
    const [role, setRole] = useState(member ? member.role : 'member');
    const [roleId, setRoleId] = useState(NO_ROLE);
    const [tri, setTri] = useState({});
    const [showOverrides, setShowOverrides] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
      if (!member) return;
      setRole(member.role);
      setRoleId(member.assigned_role ? String(member.assigned_role.id) : NO_ROLE);
      const initialTri = triStateFromOverrides(member.permission_overrides);
      setTri(initialTri);
      setShowOverrides(Object.keys(initialTri).length > 0);
      setBusy(false);
    }, [member]);

    if (!member) return null;

    const isAdmin = role === 'admin';
    const hasRoles = roles.length > 0;
    const nextOverrides = overridesFromTriState(tri);

    // patch — только изменённые поля (omission-семантика D11); для администратора
    // роль/исключения не трогаем (скрыты, не действуют). Сборка — чистая функция
    // buildMemberPatch (adapters/settings.js), покрыта vitest'ом.
    const buildPatch = () => buildMemberPatch(member, { role, roleId, overrides: nextOverrides });
    const dirty = Object.keys(buildPatch()).length > 0;

    const submit = async () => {
      if (busy || !dirty) return;
      setBusy(true);
      try {
        await Promise.resolve(onSave && onSave(member, buildPatch()));
        onClose && onClose();
      } catch {
        // Ошибку показал тостом TeamTab (onSave пробрасывает reject): модалку
        // НЕ закрываем — набранная матрица исключений остаётся у пользователя.
      } finally { setBusy(false); }
    };

    const roleOptions = [
      { value: NO_ROLE, label: 'Без роли (полный доступ)' },
      ...roles.map((r) => ({ value: String(r.id), label: r.name })),
    ];
    const overridesCount = Object.keys(nextOverrides).length;

    return (
      <Modal open={open} onClose={onClose} title="Изменить участника" size="md"
        footer={<>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button variant="primary" iconLeft="check" loading={busy} disabled={busy || !dirty} onClick={submit}>Сохранить</Button>
        </>}>
        <div className="stack stack-16">
          <Labeled label="Участник" value={member.full_name} />
          <Labeled label="Email" value={member.email} mono />
          <Field label="Статус" hint="Администратор управляет командой и тарифом">
            <Segmented value={role} onChange={setRole} options={ROLE_OPTIONS} ariaLabel="Статус участника" />
          </Field>

          {isAdmin ? (
            <span className="t-body-sm text-mute">Администратор видит всё — роль и индивидуальные исключения не действуют.</span>
          ) : !hasRoles ? (
            // Ролей в ws нет — Select и исключения не показываем (как в InviteMemberModal):
            // без ролей ограничивать нечего, остаётся только смена статуса.
            null
          ) : (
            <>
              <Field label="Роль" hint="Роль ограничивает доступ участника к аналитике">
                <Select value={roleId} onChange={setRoleId} options={roleOptions}
                  searchable={false} ariaLabel="Роль участника" placeholder="Без роли (полный доступ)" />
              </Field>

              <div className="stack stack-8">
                <Button variant="ghost" size="sm"
                  iconLeft={showOverrides ? 'chevron-up' : 'chevron-down'}
                  onClick={() => setShowOverrides((v) => !v)}>
                  Индивидуальные исключения{overridesCount ? ` (${overridesCount})` : ''}
                </Button>
                {showOverrides && (
                  <>
                    <span className="t-caption text-mute">
                      «Наследовать» — как в роли (без роли — разрешено). «Разрешить»/«Запретить»
                      действуют поверх роли только для этого участника.
                    </span>
                    {ACCESS_TABS.map((t) => (
                      <div key={t.key} className="stack stack-8" style={tabBoxStyle}>
                        <TriRow strong label={'Вкладка «' + t.label + '»'}
                          value={tri[accessKey(t.key)]}
                          onChange={(v) => setTri((s) => ({ ...s, [accessKey(t.key)]: v }))} />
                        {(ACCESS_SECTIONS[t.key] || []).map((s) => (
                          <TriRow key={s.key} label={s.label}
                            value={tri[accessKey(t.key, s.key)]}
                            onChange={(v) => setTri((st) => ({ ...st, [accessKey(t.key, s.key)]: v }))} />
                        ))}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    );
  }

export { EditMemberModal };
